import { Tooltip } from 'bootstrap';
import { useEffect, useRef } from 'react';
import { AlumniRow } from './BurnerAnalysisService';
import { coverageColor, formatDate, relativeAgo } from './utils';
import './burner-analysis.css';

interface Props {
    rows: AlumniRow[];
    totalUnfiltered: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    onRunAlumni: (alumni: AlumniRow) => void;
}

const QUAL_STYLE: Record<string, { bg: string; fg: string }> = {
    High: { bg: '#eaf3de', fg: '#27500a' },
    Moderate: { bg: '#faeeda', fg: '#633806' },
    Low: { bg: '#fcebeb', fg: '#791f1f' },
};

const tipAttrs = (tooltip?: string | null) =>
    tooltip
        ? {
              'data-bs-toggle': 'tooltip',
              'data-bs-placement': 'top',
              'data-bs-html': 'true',
              'data-bs-title': tooltip,
              'data-bs-custom-class': 'burner-analysis-tooltip',
          }
        : {};

const QualPill = ({ value, tooltip }: { value: string | null; tooltip?: string | null }) => {
    if (!value) return <span className="text-muted">—</span>;
    const s = QUAL_STYLE[value];
    if (!s) return <span {...tipAttrs(tooltip)}>{value}</span>;
    return (
        <span
            {...tipAttrs(tooltip)}
            style={{
                background: s.bg,
                color: s.fg,
                padding: '2px 8px',
                borderRadius: 100,
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                cursor: tooltip ? 'help' : 'default',
            }}>
            {value}
        </span>
    );
};

const NumScore = ({ value, tooltip }: { value: number | null; tooltip?: string | null }) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>;
    const color = coverageColor(value);
    return (
        <div
            className="d-flex align-items-center"
            {...tipAttrs(tooltip)}
            style={{ gap: 5, cursor: tooltip ? 'help' : 'default' }}>
            <span style={{ fontWeight: 600, fontSize: 13, color, minWidth: 22 }}>{value}</span>
            <div style={{ width: 38, height: 4, background: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${value}%`, height: '100%', background: color, borderRadius: 2 }} />
            </div>
        </div>
    );
};

const DS_KEYS: Array<{ key: 'pi' | 'pm' | 'el' | 'ol' | 'sm'; label: string }> = [
    { key: 'pi', label: 'PI' },
    { key: 'pm', label: 'PM' },
    { key: 'el', label: 'EL' },
    { key: 'ol', label: 'OL' },
    { key: 'sm', label: 'SM' },
];

const DataSources = ({ ds }: { ds: AlumniRow['dataSources'] }) => (
    <div className="d-flex justify-content-center" style={{ gap: 3 }}>
        {DS_KEYS.map(({ key, label }) => {
            const v = ds ? ds[key] : null;
            const style = v === null ? { bg: '#f1f5f9', fg: '#94a3b8' } : v ? { bg: '#eaf3de', fg: '#27500a' } : { bg: '#fcebeb', fg: '#791f1f' };
            return (
                <div key={key} className="text-center">
                    <div className="font-500" style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>
                        {label}
                    </div>
                    <div
                        style={{
                            background: style.bg,
                            color: style.fg,
                            width: 18,
                            height: 18,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 600,
                            margin: '0 auto',
                        }}>
                        {v === null ? '—' : v ? 'Y' : 'N'}
                    </div>
                </div>
            );
        })}
    </div>
);

const RunButton = ({ alumni, onClick }: { alumni: AlumniRow; onClick: () => void }) => {
    if (!alumni.isRunAllowed && alumni.cooldownDaysRemaining > 0) {
        return (
            <button
                type="button"
                className="btn btn-sm"
                disabled
                title={`Available in ${alumni.cooldownDaysRemaining}d (cooldown: 1 month)`}
                style={{
                    background: '#f8fafc',
                    border: '0.5px solid #cbd5e1',
                    color: '#94a3b8',
                    cursor: 'not-allowed',
                }}>
                In {alumni.cooldownDaysRemaining}d
            </button>
        );
    }
    return (
        <button
            type="button"
            className="btn btn-sm"
            onClick={onClick}
            title={alumni.lastAnalysisAt ? `Last run: ${formatDate(alumni.lastAnalysisAt)}` : 'Last run: Never'}
            style={{
                background: '#e6f1fb',
                border: '0.5px solid #185fa5',
                color: '#0c447c',
                fontWeight: 500,
            }}>
            ▶ Run analysis
        </button>
    );
};

const formatPhone = (cc: string | null, num: string | null): string => {
    if (!num) return '—';
    const masked = num.length > 4 ? `${num.slice(0, 2)}****${num.slice(-2)}` : num;
    const prefix = cc ? `+${cc}` : '';
    return `${prefix}-${masked}`;
};

const AlumniGrid = ({ rows, totalUnfiltered, hasMore, isLoadingMore, onLoadMore, onRunAlumni }: Props) => {
    const sentinelRef = useRef<HTMLTableRowElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !isLoadingMore) onLoadMore();
        });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, onLoadMore, rows.length]);

    useEffect(() => {
        if (!containerRef.current) return;
        const els = containerRef.current.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tips = Array.from(els).map((el) => new Tooltip(el));
        return () => tips.forEach((t) => t.dispose());
    }, [rows]);

    return (
        <div
            ref={containerRef}
            className="white-block white-block-notopborderradius p-0 mb-3"
            style={{ overflow: 'hidden' }}>
            <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom flex-wrap gap-2">
                <span className="font-13 font-500">
                    {Number(totalUnfiltered).toLocaleString()} profiles
                    {rows.length < totalUnfiltered && (
                        <span className="text-muted ms-1" style={{ fontWeight: 400 }}>
                            ({rows.length} shown)
                        </span>
                    )}
                </span>
                <span className="font-11 text-muted">
                    Cooldown: 1 month per profile &nbsp;·&nbsp; PI = Profile info &nbsp; PM = Profile markers &nbsp; EL =
                    Engagement log &nbsp; OL = Outreach log &nbsp; SM = Social media
                </span>
            </div>
            <div className="table-responsive">
                <table className="table mb-0" style={{ minWidth: 1080 }}>
                    <thead style={{ background: '#e6f1fb' }}>
                        <tr>
                            <th style={{ minWidth: 170 }}>Alumni name &amp; email</th>
                            <th style={{ minWidth: 90 }}>Phone</th>
                            <th style={{ minWidth: 140 }}>Program / level / year</th>
                            <th className="text-center" style={{ minWidth: 90 }}>Alma mater pride</th>
                            <th className="text-center" style={{ minWidth: 90 }}>Marketability</th>
                            <th className="text-center" style={{ minWidth: 80 }}>Vibrancy</th>
                            <th style={{ minWidth: 80 }}>Donor</th>
                            <th style={{ minWidth: 80 }}>Mentor</th>
                            <th style={{ minWidth: 80 }}>Recruiter</th>
                            <th className="text-center" style={{ minWidth: 105 }}>Last analysis</th>
                            <th className="text-center" style={{ minWidth: 130 }}>Data sources</th>
                            <th className="text-center" style={{ minWidth: 110 }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && !isLoadingMore && (
                            <tr>
                                <td colSpan={12} className="text-center text-muted py-4">
                                    No alumni profiles match the current filters.
                                </td>
                            </tr>
                        )}
                        {rows.map((a) => (
                            <tr key={a.alumniId}>
                                <td>
                                    {a.alumniId ? (
                                        <a
                                            href={`/alumni/${a.alumniId}/profile`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="tablink font-600"
                                            style={{ textDecoration: 'none' }}>
                                            {a.alumniName || '—'}
                                        </a>
                                    ) : (
                                        <div className="font-600">{a.alumniName || '—'}</div>
                                    )}
                                    <div className="font-11 text-muted">{a.email}</div>
                                </td>
                                <td className="font-12 text-muted">{formatPhone(a.mobileCountryCode, a.mobileNumber)}</td>
                                <td>
                                    <div className="font-12">{a.programName ?? '—'}</div>
                                    <div className="font-11 text-muted">
                                        {a.classCode || '—'} &nbsp;·&nbsp; {a.passOutYear ?? '—'}
                                    </div>
                                </td>
                                <td className="text-center">
                                    <QualPill
                                        value={a.burnerScores?.almaMaterPride ?? null}
                                        tooltip={a.burnerScores?.tooltipAlmaMaterPride}
                                    />
                                </td>
                                <td className="text-center">
                                    <QualPill
                                        value={a.burnerScores?.marketabilityQuotient ?? null}
                                        tooltip={a.burnerScores?.tooltipMarketability}
                                    />
                                </td>
                                <td className="text-center">
                                    <QualPill
                                        value={a.burnerScores?.vibrancyLevel ?? null}
                                        tooltip={a.burnerScores?.tooltipVibrancy}
                                    />
                                </td>
                                <td>
                                    <NumScore value={a.burnerScores?.donor ?? null} tooltip={a.burnerScores?.tooltipDonor} />
                                </td>
                                <td>
                                    <NumScore value={a.burnerScores?.mentor ?? null} tooltip={a.burnerScores?.tooltipMentor} />
                                </td>
                                <td>
                                    <NumScore value={a.burnerScores?.recruiter ?? null} tooltip={a.burnerScores?.tooltipRecruiter} />
                                </td>
                                <td className="text-center">
                                    {a.lastAnalysisAt ? (
                                        <>
                                            <div className="font-12">{formatDate(a.lastAnalysisAt)}</div>
                                            <div className="font-11 text-muted">{relativeAgo(a.lastAnalysisAt)}</div>
                                        </>
                                    ) : (
                                        <span className="font-12 text-muted">Never</span>
                                    )}
                                </td>
                                <td className="text-center">
                                    <DataSources ds={a.dataSources} />
                                </td>
                                <td className="text-center">
                                    <RunButton alumni={a} onClick={() => onRunAlumni(a)} />
                                </td>
                            </tr>
                        ))}
                        {hasMore && (
                            <tr ref={sentinelRef}>
                                <td colSpan={12} className="text-center text-muted font-12 py-2">
                                    {isLoadingMore ? 'Loading more profiles…' : 'Scroll to load more'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AlumniGrid;
